/**
 * useTableHub — abertura do Hub da Mesa e leitura da conta (só leitura).
 *
 * Numa primeira leitura de QR/NFC (navegador limpo) o Hub NÃO exige
 * sessão prévia: fica suprimido enquanto o modal atual de nome/telefone/
 * pessoas/consentimento estiver aberto e aparece logo que a sessão é
 * criada. Fecha quando o cliente escolhe "PEDIR EN LA MESA"; a escolha é
 * lembrada por sessão, para o menu não voltar a ser tapado.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { hubDismissKey, shouldOpenHub } from "@/utils/tableAssistState";

function readDismissed(tableId, sessionId) {
  try {
    return sessionStorage.getItem(hubDismissKey(tableId, sessionId)) === "1";
  } catch {
    return false;
  }
}

export function useTableHub({ API, restaurantId, tableId, sessionId, isTableOrder, sessionModalOpen }) {
  const [dismissed, setDismissed] = useState(() => readDismissed(tableId, sessionId));
  const [showBill, setShowBill] = useState(false);
  const [bill, setBill] = useState(null);
  const [billLoading, setBillLoading] = useState(false);
  const [billError, setBillError] = useState(null);

  // Sessão nova na mesma mesa → Hub volta a abrir.
  useEffect(() => {
    setDismissed(readDismissed(tableId, sessionId));
    setShowBill(false);
    setBill(null);
  }, [tableId, sessionId]);

  const closeHub = useCallback(() => {
    try {
      sessionStorage.setItem(hubDismissKey(tableId, sessionId), "1");
    } catch { /* sessionStorage indisponível — o estado em memória basta */ }
    setDismissed(true);
    setShowBill(false);
  }, [tableId, sessionId]);

  const openHub = useCallback(() => {
    try {
      sessionStorage.removeItem(hubDismissKey(tableId, sessionId));
    } catch { /* idem */ }
    setDismissed(false);
  }, [tableId, sessionId]);

  const loadBill = useCallback(async () => {
    if (!API || !restaurantId || !tableId || !sessionId) return;
    setBillLoading(true);
    setBillError(null);
    try {
      const res = await axios.get(`${API}/public/table-assist/bill`, {
        params: { restaurant_id: restaurantId, table_id: tableId, session_id: sessionId },
      });
      setBill(res.data || null);
    } catch (err) {
      setBillError(err?.response?.data?.detail || "No se pudo cargar tu cuenta.");
    }
    setBillLoading(false);
  }, [API, restaurantId, tableId, sessionId]);

  return {
    hubOpen: shouldOpenHub({ isTableOrder, tableId, dismissed, sessionModalOpen }),
    // Basta tenant + mesa validados: o Hub não espera por sessão prévia.
    hubAvailable: !!(isTableOrder && tableId),
    openHub, closeHub,
    showBill, setShowBill,
    bill, billLoading, billError, loadBill,
  };
}
